import React, { useEffect, useRef } from 'react';
import { Chart, registerables } from 'chart.js';

Chart.register(...registerables);

export default function LogsChart({ data }) {
  const timelineChartRef = useRef(null);
  const actionsChartRef = useRef(null);
  const usersChartRef = useRef(null);
  const timelineChartInstance = useRef(null);
  const actionsChartInstance = useRef(null);
  const usersChartInstance = useRef(null);

  const chartOptions = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: {
        labels: {
          color: 'rgba(255, 255, 255, 0.8)',
          font: {
            size: 12
          }
        }
      }
    },
    scales: {
      x: {
        ticks: {
          color: 'rgba(255, 255, 255, 0.6)',
          font: {
            size: 11
          }
        },
        grid: {
          color: 'rgba(132, 169, 140, 0.1)'
        }
      },
      y: {
        ticks: {
          color: 'rgba(255, 255, 255, 0.6)',
          font: {
            size: 11
          }
        },
        grid: {
          color: 'rgba(132, 169, 140, 0.1)'
        }
      }
    }
  };

  useEffect(() => {
    // Timeline Chart
    if (timelineChartRef.current && data.timeline) {
      const ctx = timelineChartRef.current.getContext('2d');
      
      if (timelineChartInstance.current) {
        timelineChartInstance.current.destroy();
      }

      timelineChartInstance.current = new Chart(ctx, {
        type: 'line',
        data: {
          labels: data.timeline.map(item => item.label || item.date),
          datasets: [
            {
              label: 'Total de Logs',
              data: data.timeline.map(item => item.total),
              borderColor: '#84a98c',
              backgroundColor: 'rgba(132, 169, 140, 0.1)',
              fill: true,
              tension: 0.4
            },
            {
              label: 'Erros',
              data: data.timeline.map(item => item.errors || 0),
              borderColor: '#dc3545',
              backgroundColor: 'rgba(220, 53, 69, 0.1)',
              fill: false,
              tension: 0.4
            },
            {
              label: 'Críticos',
              data: data.timeline.map(item => item.critical || 0),
              borderColor: '#ffc107',
              backgroundColor: 'rgba(255, 193, 7, 0.1)',
              fill: false,
              tension: 0.4
            }
          ]
        },
        options: {
          ...chartOptions,
          plugins: {
            ...chartOptions.plugins,
            title: {
              display: true,
              text: 'Atividade ao Longo do Tempo',
              color: '#84a98c',
              font: {
                size: 14,
                weight: 'bold'
              }
            }
          }
        }
      });
    }

    return () => {
      if (timelineChartInstance.current) {
        timelineChartInstance.current.destroy();
      }
    };
  }, [data.timeline]);

  useEffect(() => {
    // Actions Chart
    if (actionsChartRef.current && data.actions) {
      const ctx = actionsChartRef.current.getContext('2d');
      
      if (actionsChartInstance.current) {
        actionsChartInstance.current.destroy();
      }

      const actionColors = [
        '#84a98c', '#0dcaf0', '#198754', '#fd7e14',
        '#6610f2', '#d63384', '#20c997', '#ffc107'
      ];

      actionsChartInstance.current = new Chart(ctx, {
        type: 'doughnut',
        data: {
          labels: data.actions.map(item => item.action_name || item.action),
          datasets: [{
            data: data.actions.map(item => item.count),
            backgroundColor: actionColors,
            borderColor: actionColors.map(color => color + '80'),
            borderWidth: 1
          }]
        },
        options: {
          ...chartOptions,
          plugins: {
            ...chartOptions.plugins,
            title: {
              display: true,
              text: 'Distribuição por Ações',
              color: '#84a98c',
              font: {
                size: 14,
                weight: 'bold'
              }
            }
          }
        }
      });
    }

    return () => {
      if (actionsChartInstance.current) {
        actionsChartInstance.current.destroy();
      }
    };
  }, [data.actions]);

  useEffect(() => {
    // Users Chart
    if (usersChartRef.current && data.users) {
      const ctx = usersChartRef.current.getContext('2d');
      
      if (usersChartInstance.current) {
        usersChartInstance.current.destroy();
      }

      usersChartInstance.current = new Chart(ctx, {
        type: 'bar',
        data: {
          labels: data.users.map(item => item.user_name || 'Sistema'),
          datasets: [{
            label: 'Quantidade de Ações',
            data: data.users.map(item => item.count),
            backgroundColor: 'rgba(132, 169, 140, 0.6)',
            borderColor: '#84a98c',
            borderWidth: 1
          }]
        },
        options: {
          ...chartOptions,
          plugins: {
            ...chartOptions.plugins,
            title: {
              display: true,
              text: 'Atividade por Usuário',
              color: '#84a98c',
              font: {
                size: 14,
                weight: 'bold'
              }
            },
            legend: {
              display: false
            }
          },
          scales: {
            ...chartOptions.scales,
            x: {
              ...chartOptions.scales.x,
              ticks: {
                ...chartOptions.scales.x.ticks,
                maxRotation: 45,
                minRotation: 45
              }
            }
          }
        }
      });
    }

    return () => {
      if (usersChartInstance.current) {
        usersChartInstance.current.destroy();
      }
    };
  }, [data.users]);

  return (
    <div className="charts-container">
      <div className="row">
        {/* Timeline Chart */}
        <div className="col-12 mb-4">
          <div className="chart-card">
            <div className="chart-wrapper" style={{ height: '300px' }}>
              <canvas ref={timelineChartRef}></canvas>
            </div>
          </div>
        </div>
        
        {/* Actions Chart */}
        <div className="col-lg-6 mb-4">
          <div className="chart-card">
            <div className="chart-wrapper" style={{ height: '350px' }}>
              <canvas ref={actionsChartRef}></canvas>
            </div>
          </div>
        </div>
        
        {/* Users Chart */}
        <div className="col-lg-6 mb-4">
          <div className="chart-card">
            <div className="chart-wrapper" style={{ height: '350px' }}>
              <canvas ref={usersChartRef}></canvas>
            </div>
          </div>
        </div>
      </div>

      <style>{`
        .charts-container .chart-card {
          background: rgba(18, 26, 23, 0.8);
          border: 1px solid rgba(132, 169, 140, 0.15);
          border-radius: 12px;
          padding: 1.5rem;
        }
        
        .charts-container .chart-wrapper {
          position: relative;
        }
        
        .charts-container canvas {
          background: rgba(13, 18, 16, 0.3);
          border-radius: 8px;
        }
      `}</style>
    </div>
  );
}
